import { useCallback, useState } from 'react';
import { useLang } from 'hooks/lang';

import { Table, IconButton, Button, Column, Row, Card } from 'components/Blocks';

import { BuildingIcon, PlusIcon, PencilIcon } from 'components/Icons';

import Form from './Form';

import { loaders, Office } from 'entities';

const defaultOffice = {
  id: '',
  name: '',
  address: '',
  city: '',
  state: '',
  country: '',
};

function Offices(): JSX.Element {
  const [focusedOffice, setFocusedOffice] = useState<Office | null>(null);

  const { errorOrLoading, data, overwrite } = loaders.offices.list.use({
    defaultValue: [],
  });

  const { t } = useLang();

  const save = useCallback(
    async (mutatedOffice: Office) => {
      const updateId = focusedOffice?.id;

      overwrite((old) =>
        updateId
          ? old.map((office) => (office.id === updateId ? mutatedOffice : office))
          : [...old, mutatedOffice],
      );
    },
    [overwrite, focusedOffice],
  );

  return (
    <Column gap="8">
      <Form close={() => setFocusedOffice(null)} save={save} office={focusedOffice} />

      <Row justifyContent="space-between">
        <h4 className="flex items-center gap-2">
          <BuildingIcon size={40} />

          {t('offices')}
        </h4>

        <Button
          size="small"
          format="square"
          round
          onClick={() => setFocusedOffice((old) => (old ? null : defaultOffice))}
        >
          <PlusIcon />
        </Button>
      </Row>

      <Card>
        <Table
          loading={errorOrLoading}
          items={data}
          itemsPerPage={20}
          uniqueKey="id"
          columns={[
            { label: t('name') as string, property: 'name', search: true },
            { label: t('address') as string, property: 'address' },
            { label: t('city') as string, property: 'city' },
            { label: t('state') as string, property: 'state' },
            { label: t('country') as string, property: 'country' },
            {
              render: (office) => (
                <Row justifyContent="flex-end">
                  <IconButton tooltipContent="edit" onClick={() => setFocusedOffice(office)}>
                    <PencilIcon size={16} />
                  </IconButton>
                </Row>
              ),
            },
          ]}
        />
      </Card>
    </Column>
  );
}

export default Offices;
